import {
  lazy,
  Suspense,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type {
  Project,
  ProjectCodingSessionHealthResponse,
  Session,
  SessionAccessInfo,
} from '@claude-code-api/shared';
import {
  AlertTriangle,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Settings,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Spinner } from '@/components/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ProjectTasksTab } from '@/components/features/projects/ProjectTasksTab';
import { ProjectTeamAiTab } from '@/components/features/projects/ProjectTeamAiTab';
import { ProjectTeamControlsCard } from '@/components/features/projects/ProjectTeamControlsCard';
import {
  DEFAULT_TASK_STATUS_VISIBILITY,
  useProjectTasksBoardStore,
  type TaskStatusVisibilityKey,
} from '@/stores/projectTasksBoardStore';

const ProjectTeamActivityLog = lazy(() =>
  import('@/components/features/projects/ProjectTeamActivityLog').then(
    (module) => ({ default: module.ProjectTeamActivityLog }),
  ),
);

const TASK_STATUS_VISIBILITY_ORDER: TaskStatusVisibilityKey[] = ['other'];

export default function ProjectWorkflowPage() {
  const { t } = useTranslation(['projects', 'session']);
  const splitContainerRef = useRef<HTMLDivElement | null>(null);
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [chatPanelWidth, setChatPanelWidth] = useState(30);
  const [chatPanelHidden, setChatPanelHidden] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState('controls');
  const [recreateLoading, setRecreateLoading] = useState(false);
  const visibleTaskStatuses = useProjectTasksBoardStore(
    (state) =>
      state.byProjectId[projectId || '']?.visibleStatuses ||
      DEFAULT_TASK_STATUS_VISIBILITY,
  );
  const setTaskStatusVisible = useProjectTasksBoardStore(
    (state) => state.setStatusVisible,
  );
  const resetTaskStatusVisibility = useProjectTasksBoardStore(
    (state) => state.resetStatusVisibility,
  );

  useEffect(() => {
    if (!projectId || typeof window === 'undefined') return;
    const savedWidth = Number(
      window.localStorage.getItem(`project-workflow-chat-width:${projectId}`),
    );
    if (Number.isFinite(savedWidth) && savedWidth >= 20 && savedWidth <= 60) {
      setChatPanelWidth(savedWidth);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId || typeof window === 'undefined') return;
    window.localStorage.setItem(
      `project-workflow-chat-width:${projectId}`,
      String(chatPanelWidth),
    );
  }, [chatPanelWidth, projectId]);

  useEffect(() => {
    if (!projectId || typeof window === 'undefined') return;
    window.localStorage.setItem(
      `project-workflow-chat-hidden:${projectId}`,
      chatPanelHidden ? '1' : '0',
    );
  }, [chatPanelHidden, projectId]);

  useEffect(() => {
    if (!projectId || typeof window === 'undefined') return;
    setChatPanelHidden(
      window.localStorage.getItem(
        `project-workflow-chat-hidden:${projectId}`,
      ) === '1',
    );
  }, [projectId]);

  useEffect(() => {
    if (!isResizing) return undefined;

    const handlePointerMove = (event: PointerEvent) => {
      const container = splitContainerRef.current;
      if (!container) return;
      const bounds = container.getBoundingClientRect();
      if (!bounds.width) return;
      const nextWidth = ((event.clientX - bounds.left) / bounds.width) * 100;
      if (nextWidth <= 8) {
        setChatPanelHidden(true);
        return;
      }
      const clampedWidth = Math.min(55, Math.max(22, nextWidth));
      setChatPanelHidden(false);
      setChatPanelWidth(clampedWidth);
    };

    const stopResizing = () => {
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResizing);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResizing);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    void fetchProject();
  }, [projectId]);

  const codingHealthQuery = useQuery<ProjectCodingSessionHealthResponse>({
    queryKey: ['project-workflow-coding-health', projectId],
    queryFn: async () => {
      const response = await fetch(
        `/api/projects/coding-session/health/${projectId}`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        throw new Error(
          t('projects:workflow.errors.loadCodingHealthWithStatus', {
            status: response.status,
          }),
        );
      }
      return response.json();
    },
    enabled: Boolean(projectId && project?.teamAiEngineVersion !== 'v2'),
    staleTime: 5_000,
    refetchInterval: 10_000,
  });

  const activeCodingSession =
    sessions.find(
      (session) =>
        session.runnerType === 'acpx-team' && session.status === 'running',
    ) ||
    sessions.find(
      (session) =>
        session.runnerType === 'acpx-team' && session.status === 'pending',
    ) ||
    null;

  const runtimeAccessQuery = useQuery<SessionAccessInfo>({
    queryKey: [
      'project-workflow-coding-runtime-access',
      activeCodingSession?.id,
    ],
    queryFn: async () => {
      const response = await fetch(
        `/api/sessions/${activeCodingSession?.id}/access`,
        { credentials: 'include' },
      );
      if (!response.ok) {
        throw new Error(
          t('projects:workflow.errors.loadCodingHealthWithStatus', {
            status: response.status,
          }),
        );
      }
      const payload = await response.json();
      return payload.data;
    },
    enabled: Boolean(
      activeCodingSession?.id && activeCodingSession.status === 'running',
    ),
    staleTime: 5_000,
    refetchInterval: 30_000,
  });

  const fetchProject = async () => {
    try {
      const [projectRes, sessionsRes] = await Promise.all([
        fetch(`/api/projects/${projectId}`, { credentials: 'include' }),
        fetch(`/api/sessions?projectId=${projectId}`, {
          credentials: 'include',
        }),
      ]);
      if (projectRes.ok) {
        const data = await projectRes.json();
        setProject(data.data);
      }
      if (sessionsRes.ok) {
        const data = await sessionsRes.json();
        setSessions(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch project workflow:', error);
      toast.error(t('projects:workflow.errors.load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const codingSessionId = codingHealthQuery.data?.codingSession.sessionId;
    if (
      codingSessionId &&
      !sessions.some((session) => session.id === codingSessionId)
    ) {
      void fetchProject();
    }
  }, [codingHealthQuery.data?.codingSession.sessionId, sessions]);

  const recreateCodingSession = async () => {
    if (!activeCodingSession?.id) return;
    setRecreateLoading(true);
    try {
      const response = await fetch(
        `/api/sessions/${activeCodingSession.id}/recreate`,
        {
          method: 'POST',
          credentials: 'include',
        },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || t('session:errors.recreate'));
        return;
      }

      let terminalStatus = '';
      for (let attempt = 0; attempt < 90; attempt += 1) {
        const statusResponse = await fetch(
          `/api/sessions/${activeCodingSession.id}/system-update/status`,
          { credentials: 'include' },
        );
        const statusJson = await statusResponse.json().catch(() => ({}));
        terminalStatus = String(statusJson?.data?.status || '');
        if (
          terminalStatus === 'ready' ||
          terminalStatus === 'rolled_back' ||
          terminalStatus === 'failed'
        ) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      if (terminalStatus === 'ready') {
        toast.success(t('session:messages.recreated'));
      } else if (terminalStatus === 'rolled_back') {
        toast.warning(t('session:errors.updateRestored'));
      } else if (terminalStatus === 'failed') {
        toast.error(t('session:errors.recreate'));
      } else {
        toast.info(
          'Update is still running. Refresh this page to check status.',
        );
      }
      await fetchProject();
      await Promise.all([
        codingHealthQuery.refetch(),
        runtimeAccessQuery.refetch(),
      ]);
    } catch (error) {
      console.error('Failed to recreate coding session:', error);
      toast.error(t('session:errors.recreate'));
    } finally {
      setRecreateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Spinner className="h-12 w-12 mx-auto" />
          <p className="mt-4 text-sm text-muted-foreground">
            {t('projects:workflow.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="rounded-xl border border-dashed bg-card/60 px-6 py-10 text-center shadow-sm">
        <h2 className="text-lg font-semibold">
          {t('projects:workflow.notFound.title')}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('projects:workflow.notFound.description')}
        </p>
        <Button asChild className="mt-4">
          <Link to="/projects">
            {t('projects:workflow.notFound.backToProjects')}
          </Link>
        </Button>
      </div>
    );
  }

  if (!project.isTeamMode) {
    return (
      <div className="rounded-xl border border-dashed bg-card/60 px-6 py-10 text-center shadow-sm">
        <h2 className="text-lg font-semibold">
          {t('projects:workflow.teamModeRequired.title')}
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('projects:workflow.teamModeRequired.description')}
        </p>
        <Button asChild className="mt-4">
          <Link to={`/projects/${project.id}`}>
            {t('projects:workflow.teamModeRequired.backToProject')}
          </Link>
        </Button>
      </div>
    );
  }

  const workflowHealth =
    project.teamAiEngineVersion === 'v2' ? undefined : codingHealthQuery.data;
  const recovery = workflowHealth?.recovery || null;
  const recoveryErrorText =
    recovery?.errorCode === 'runner_image_missing'
      ? t('projects:workflow.recovery.runnerImageMissing')
      : recovery?.error || null;
  const codingSession = activeCodingSession;
  const codingRuntime =
    runtimeAccessQuery.data?.runtime || codingSession?.metadata || null;
  const showCodingRuntimeUpdateBanner =
    !project.isShared &&
    codingSession?.status === 'running' &&
    codingRuntime?.systemUpdateAvailable;
  const currentSystemVersion =
    codingRuntime?.systemVersion || t('session:updateBanner.unknownVersion');
  const latestSystemVersion =
    codingRuntime?.latestSystemVersion ||
    t('session:updateBanner.unknownVersion');
  const chatWidthStyle = {
    '--workflow-chat-width': chatPanelHidden ? '0%' : `${chatPanelWidth}%`,
  } as CSSProperties;

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      {showCodingRuntimeUpdateBanner ? (
        <div className="mx-3 mt-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 sm:mx-4 lg:mx-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 space-y-1">
              <h2 className="text-sm font-semibold text-amber-950">
                {t('session:updateBanner.title')}
              </h2>
              <p className="text-sm text-amber-900/90">
                {t('session:updateBanner.description')}
              </p>
              <p className="text-xs text-amber-900/80">
                {t('session:updateBanner.version', {
                  current: currentSystemVersion,
                  latest: latestSystemVersion,
                })}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => void recreateCodingSession()}
              disabled={recreateLoading}
              className="shrink-0"
            >
              {recreateLoading ? (
                <Spinner className="mr-1 h-4 w-4" />
              ) : (
                <RefreshCw className="mr-1 h-4 w-4" />
              )}
              {t('session:actions.recreate')}
            </Button>
          </div>
        </div>
      ) : null}

      <div
        ref={splitContainerRef}
        className="flex h-full min-h-0 w-full flex-1 flex-col gap-2 px-3 py-3 sm:px-4 lg:px-5 xl:flex-row xl:items-stretch xl:gap-0"
      >
        <div
          className={
            chatPanelHidden
              ? 'hidden xl:hidden'
              : 'flex min-h-0 min-w-0 flex-col gap-2 xl:self-stretch xl:w-[var(--workflow-chat-width)] xl:shrink-0'
          }
          style={chatWidthStyle}
        >
          <div className="flex flex-wrap items-center gap-3 px-1">
            <Button
              asChild
              variant="ghost"
              size="icon"
              aria-label={t('projects:workflow.actions.backToProject')}
              className="shrink-0"
            >
              <Link to={`/projects/${project.id}`}>
                <ArrowLeft className="h-5 w-5" />
              </Link>
            </Button>
            <h1 className="text-2xl font-heading font-bold leading-none">
              {project.name}
            </h1>
            {recovery?.attempted ? (
              recovery.recovered ? (
                <Badge variant="secondary" className="h-8 gap-1.5 self-center">
                  <RefreshCw className="h-3.5 w-3.5" />
                  {t('projects:workflow.recovery.recovered')}
                </Badge>
              ) : recovery.error ? (
                <Badge
                  variant="destructive"
                  className="h-8 gap-1.5 self-center"
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {t('projects:workflow.recovery.failed')}
                </Badge>
              ) : null
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('projects:workflow.settings.title')}
              className="ml-auto shrink-0"
              onClick={() => setSettingsOpen(true)}
            >
              <Settings className="h-5 w-5" />
            </Button>
          </div>

          {workflowHealth?.codingSession.health === 'degraded' ? (
            <p className="px-1 text-sm text-amber-600">
              {t('projects:workflow.recovery.degraded')}
            </p>
          ) : recoveryErrorText ? (
            <p className="px-1 text-sm text-destructive">
              {t('projects:workflow.recovery.error', {
                error: recoveryErrorText,
              })}
            </p>
          ) : null}

          <div className="flex min-h-0 flex-1 min-w-0">
            <ProjectTeamAiTab projectId={project.id} />
          </div>
        </div>

        <button
          type="button"
          aria-label={
            chatPanelHidden
              ? t('projects:workflow.splitter.showChat')
              : t('projects:workflow.splitter.resizePanels')
          }
          aria-valuemin={22}
          aria-valuemax={55}
          aria-valuenow={chatPanelHidden ? 0 : Math.round(chatPanelWidth)}
          onPointerDown={(event) => {
            if (chatPanelHidden) return;
            if (event.button !== 0) return;
            event.preventDefault();
            setIsResizing(true);
          }}
          onClick={() => {
            if (chatPanelHidden) {
              setChatPanelHidden(false);
              return;
            }
            setChatPanelHidden(true);
          }}
          className={`group relative hidden shrink-0 items-center justify-center xl:flex ${
            chatPanelHidden ? 'w-8 cursor-pointer' : 'w-4 cursor-col-resize'
          }`}
        >
          <span className="h-full w-px bg-border/80 transition-colors group-hover:bg-foreground/20" />
          <span
            className={`absolute rounded-full border border-border/80 bg-background shadow-sm transition-colors group-hover:border-foreground/20 ${
              chatPanelHidden ? 'h-24 w-6' : 'h-16 w-2'
            }`}
          />
          <span className="absolute flex items-center justify-center text-muted-foreground">
            {chatPanelHidden ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </span>
        </button>

        <div
          className={`flex min-h-0 min-w-0 flex-1 xl:self-stretch ${
            chatPanelHidden ? '' : 'xl:pl-2'
          }`}
        >
          <ProjectTasksTab projectId={project.id} layout="embedded" />
        </div>
      </div>

      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto p-0">
          <DialogHeader className="border-b px-5 py-4">
            <DialogTitle>{t('projects:workflow.settings.title')}</DialogTitle>
          </DialogHeader>
          <div className="p-5">
            <Tabs value={settingsTab} onValueChange={setSettingsTab}>
              <TabsList className="w-full justify-start">
                <TabsTrigger value="controls">
                  {t('projects:workflow.settings.tabs.controls')}
                </TabsTrigger>
                <TabsTrigger value="logs">
                  {t('projects:workflow.settings.tabs.logs')}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="controls" className="mt-4">
                <div className="mb-4 rounded-xl border border-border/70 bg-card/70 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold">
                        {t('projects:workflow.settings.statusVisibility.title')}
                      </div>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {t(
                          'projects:workflow.settings.statusVisibility.description',
                        )}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 shrink-0 px-2 text-xs"
                      onClick={() =>
                        projectId && resetTaskStatusVisibility(projectId)
                      }
                    >
                      {t('projects:workflow.settings.statusVisibility.reset')}
                    </Button>
                  </div>
                  <div className="mt-4 grid gap-3">
                    {TASK_STATUS_VISIBILITY_ORDER.map((status) => {
                      const label = t(
                        `projects:workflow.settings.statusVisibility.statuses.${status}.label`,
                      );
                      const description = t(
                        `projects:workflow.settings.statusVisibility.statuses.${status}.description`,
                      );
                      return (
                        <label
                          key={status}
                          className="flex cursor-pointer items-center justify-between gap-4 rounded-lg border border-border/60 bg-background/70 px-3 py-2.5"
                        >
                          <span className="min-w-0">
                            <span className="block text-sm font-medium">
                              {label}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              {description}
                            </span>
                          </span>
                          <Switch
                            checked={visibleTaskStatuses[status]}
                            onCheckedChange={(checked) =>
                              projectId &&
                              setTaskStatusVisible(projectId, status, checked)
                            }
                            aria-label={t(
                              'projects:workflow.settings.statusVisibility.showStatus',
                              { label },
                            )}
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>
                <ProjectTeamControlsCard
                  project={project}
                  onProjectUpdated={() => {
                    void fetchProject();
                  }}
                />
              </TabsContent>
              <TabsContent value="logs" className="mt-4">
                <Suspense
                  fallback={
                    <div className="flex h-36 items-center justify-center text-sm text-muted-foreground">
                      <Spinner className="mr-2 h-4 w-4" />
                      {t('projects:workflow.logs.loading')}
                    </div>
                  }
                >
                  <ProjectTeamActivityLog
                    projectId={project.id}
                    enabled={settingsOpen && settingsTab === 'logs'}
                  />
                </Suspense>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
